import { useState, useEffect, useMemo, useRef } from 'react';
import { fetchTickets } from '../Pages/AddCompanies/Action/action';
import { MapPoint, ClusterPoint, LocationType, LocationLabel } from '../Interfaces/Map';
import { ShowAlert } from '../GlobalComponent/Alerts/Alert';
import { Company } from '../Interfaces/Company';
import { isValidCoordinate, fetchCoordinates } from "../GlobalComponent/MapView/mapUtils";

interface Ticket {
  _id: string;
  detectedLocation: {
    geoLocation: {
      coordinates: [number, number]; // [lat, lng]
    };
    address?: string;
  };
  companyId: string;
  companyName: string;
  createdAt: string;
  updatedAt: string;
}

const useTicketMap = ({ companies }: { companies: Company[] }) => {
  const [mapPoints, setMapPoints] = useState<MapPoint[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [clusters, setClusters] = useState<ClusterPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isTicketMode, setIsTicketMode] = useState<boolean>(true);

  useEffect(() => {
    const loadTickets = async () => {
      try {
        setLoading(true);
        // Clear existing points when switching modes
        setMapPoints([]);
        setClusters([]);
        
        const queryParams = selectedCompany ? { companyId: selectedCompany, isTicketMode: isTicketMode } : { isTicketMode: isTicketMode };
        const response = await fetchTickets(queryParams);

        // Handle the response data structure properly
        const ticketData = response?.data || response || [];
        const points: MapPoint[] = generateMapPoints(ticketData);

        if (points && Array.isArray(points)) {
          setMapPoints(points);
        } else {
          ShowAlert('error', 'No valid ticket data found');
          setMapPoints([]);
        }
      } catch (error) {
        setMapPoints([]);
        ShowAlert('error', 'Error fetching tickets');
      } finally {
        setLoading(false);
      }
    };

    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, isTicketMode]);

  const generateMapPoints = (tickets: Ticket[]): MapPoint[] => {
    const points: MapPoint[] = [];
    const seenIds = new Set<string>();

    tickets.forEach(ticket => {
      // Skip if no detected location
      if (!ticket.detectedLocation?.geoLocation?.coordinates) {
        console.warn(`Skipping ticket ${ticket._id}: missing detected location`);
        return;
      }

      // Validate coordinates
      const [lat, lng] = fetchCoordinates(ticket.detectedLocation);

      if (!isValidCoordinate(lat, lng)) {
        console.warn(`Skipping ticket ${ticket._id}: invalid coordinates`);
        return;
      }

      // Add ticket point
      const pointId = `${ticket._id}-ticket`;
      if (!seenIds.has(pointId)) {
        seenIds.add(pointId);
        points.push({
          id: pointId,
          lat,
          lng,
          type: isTicketMode ? LocationType.TICKET : LocationType.DOCTOR_CONSULT,
          ticketId: ticket._id,
          companyName: ticket.companyName,
          address: ticket.detectedLocation.address || (isTicketMode ? LocationLabel.TICKET : LocationLabel.DOCTOR_CONSULT)
        });
      }
    });
    return points;
  };

  const filteredCompanies = useMemo(() => {
    return companies.filter(company =>
      company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  const selectedCompanyName = companies.find(c => c._id === selectedCompany)?.companyName || "";

  // Clustering logic
  const createClusters = (points: MapPoint[], distance: number = 0.01): ClusterPoint[] => {
    const clusters: ClusterPoint[] = [];
    const processed = new Set<string>();

    points.forEach(point => {
      if (processed.has(point.id)) return;

      const nearbyPoints = points.filter(p => {
        if (processed.has(p.id) || p.id === point.id) return false;
        const dist = Math.sqrt(
          Math.pow(p.lat - point.lat, 2) + Math.pow(p.lng - point.lng, 2)
        );
        return dist <= distance;
      });

      if (nearbyPoints.length > 0) {
        const clusterPoints = [point, ...nearbyPoints];
        const avgLat = clusterPoints.reduce((sum, p) => sum + p.lat, 0) / clusterPoints.length;
        const avgLng = clusterPoints.reduce((sum, p) => sum + p.lng, 0) / clusterPoints.length;

        clusters.push({
          id: `cluster-${clusters.length}`,
          lat: avgLat,
          lng: avgLng,
          count: clusterPoints.length,
          points: clusterPoints,
          type: isTicketMode ? LocationType.TICKET : LocationType.DOCTOR_CONSULT
        });

        clusterPoints.forEach(p => processed.add(p.id));
      } else {
        clusters.push({
          id: `single-${point.id}`,
          lat: point.lat,
          lng: point.lng,
          count: 1,
          points: [point],
          type: point.type
        });
        processed.add(point.id);
      }
    });

    return clusters;
  };

  // Update clusters when mapPoints change
  useEffect(() => {
    const newClusters = createClusters(mapPoints);
    setClusters(newClusters);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapPoints]);

  return {
    // State
    loading,
    companies,
    selectedCompany,
    setSelectedCompany,
    searchTerm,
    setSearchTerm,
    isOpen,
    setIsOpen,
    inputRef,
    mapPoints,
    clusters,
    isTicketMode,
    setIsTicketMode,

    // Computed values
    filteredCompanies,
    selectedCompanyName,
  };
};

export default useTicketMap;